// Step 5 — Import Control intake review (app.md §9). Queue of invoices held at
// Pending Import Control Decision, each tagged with its unresolved flag types.
// This is a click-into-each-invoice surface (no bulk) — each held invoice needs
// different fixes. The detail modal opens in EDIT mode (the only place values
// are editable), and its Save & Release both saves and releases in one request.
//
// PORT NOTE: release used to be a decision write the reviewer could only hope
// had landed — a background process advanced the invoice, so the row sat at
// "Submitting…" while the queue re-polled for up to half a minute. The release is
// applied synchronously now, so acting on an invoice means reloading the queue
// and finding it gone. The flag chips read each row's own unresolved flag types
// (there was a second query per queue load for them).
import { useMemo, useState } from 'react'
import { Body1, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { ScreenTitle } from '../components/ScreenTitle'
import type { CurrentUser, Invoice } from '../lib/types'
import { InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  user: CurrentUser
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ImportControlScreen({ user, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingImportControl], refreshNonce)
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  // The release already happened by the time this runs — confirm it, then reload
  // so the invoice drops out of the queue.
  function handleReleased() {
    dispatchToast(
      <Toast>
        <ToastTitle>Invoice released for approval</ToastTitle>
      </Toast>,
      { intent: 'success' },
    )
    void reload()
    onQueuesChanged?.()
  }

  function handleArchived() {
    dispatchToast(
      <Toast>
        <ToastTitle>Archived — Not an Invoice</ToastTitle>
      </Toast>,
      { intent: 'success' },
    )
    void reload()
    onQueuesChanged?.()
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Import Control</ScreenTitle>
        <Body1 className={s.sub}>For each flagged invoice, resolve all outstanding issues to release for approval.</Body1>
      </div>

      <ErrorBanner message={error} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue invoices={filtered} showFlags onOpen={setOpenInvoice} />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        editable
        user={user}
        onClose={() => setOpenInvoice(null)}
        onChanged={() => void reload({ silent: true })}
        onReleased={handleReleased}
        onArchived={handleArchived}
      />
    </div>
  )
}
